import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Home, AlertCircle } from "lucide-react";

const API = window.location.origin + "/api";

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

export default function LoginPage() {
  const [msLoading, setMsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const msError = searchParams.get("error");

  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/microsoft`);
      window.location.href = data.auth_url;
    } catch (err) {
      setError("Impossible de contacter Microsoft. Vérifiez la configuration Azure.");
      setMsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Left — Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 bg-white">
        <div className="max-w-sm w-full mx-auto">

          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              ListeAttente
            </span>
          </div>

          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Bienvenue
          </h1>
          <p className="text-slate-500 text-sm mb-10">
            Connectez-vous avec votre compte Microsoft 365 pour accéder à la liste d'attente.
          </p>

          <button
            onClick={handleMicrosoftLogin}
            disabled={msLoading}
            data-testid="microsoft-login-btn"
            className="w-full flex items-center justify-center gap-3 px-5 py-3.5 border border-slate-300 rounded-xl text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400 transition-all disabled:opacity-50 shadow-sm"
          >
            {msLoading ? (
              <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <MicrosoftIcon />
            )}
            {msLoading ? "Redirection vers Microsoft..." : "Se connecter avec Microsoft 365"}
          </button>

          {(error || msError) && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mt-5" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error || msError}</span>
            </div>
          )}

          <p className="text-xs text-slate-400 text-center mt-8">
            Seuls les comptes autorisés par l'administrateur peuvent se connecter.
          </p>
        </div>
      </div>

      {/* Right — Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url(https://images.pexels.com/photos/950241/pexels-photo-950241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=940)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}>
        <div className="absolute inset-0 bg-indigo-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Gestion de la liste d'attente
          </h2>
          <p className="text-indigo-100 text-sm leading-relaxed max-w-sm">
            Gérez efficacement les demandes de logement social avec un suivi en temps réel.
          </p>
          <div className="flex gap-4 mt-6">
            {[
              { color: "bg-slate-400", label: "Sur liste" },
              { color: "bg-blue-400", label: "En cours" },
              { color: "bg-red-400", label: "À supprimer" },
              { color: "bg-orange-400", label: "À revoir" },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                <span className="text-xs text-indigo-100">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
